import { buildPlaceholderToken } from '@pbk-web/utils';
import type {
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  LexicalUpdateJSON,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $create, DecoratorNode } from 'lexical';
import type { JSX } from 'react';

import { DynamicDataChip } from './DynamicDataChip';

// `type` and `version` are contributed by SerializedLexicalNode via super.exportJSON().
export type SerializedDynamicDataNode = Spread<
  {
    id: string;
  },
  SerializedLexicalNode
>;

/**
 * An inline, uneditable placeholder chip, saved as a plain `${{key}}` token (as Froala saves it).
 * - `data-lexical-dynamic-data` spans from earlier saves still load.
 */
export class DynamicDataNode extends DecoratorNode<JSX.Element> {
  __id: string;

  // The id must default so the constructor takes no required arguments: Lexical
  // synthesises static clone()/importJSON() from $config() by calling `new this()`.
  constructor(id = '', key?: NodeKey) {
    super(key);
    this.__id = id;
  }

  override $config() {
    return this.config('dynamic-data', {
      importDOM: {
        span: (domNode: HTMLElement) => {
          if (!domNode.hasAttribute('data-lexical-dynamic-data')) return null;
          return {
            conversion: (element: HTMLElement): DOMConversionOutput => {
              const id = element.getAttribute('data-lexical-dynamic-data');
              if (!id) return { node: null };
              return { node: $createDynamicDataNode(id) };
            },
            priority: 2 as const,
          };
        },
      },
    });
  }

  // Replaces the static clone() copy step: the synthesised clone default-constructs,
  // so every own property has to be carried over here.
  override afterCloneFrom(prevNode: this): void {
    super.afterCloneFrom(prevNode);
    this.__id = prevNode.__id;
  }

  getId(): string {
    return this.getLatest().__id;
  }

  setId(id: string): this {
    const self = this.getWritable();
    self.__id = id;
    return self;
  }

  // ---------- Serialization ----------

  override exportJSON(): SerializedDynamicDataNode {
    return {
      ...super.exportJSON(),
      id: this.__id,
    };
  }

  override updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedDynamicDataNode>): this {
    return super.updateFromJSON(serializedNode).setId(serializedNode.id);
  }

  // ---------- DOM handling ----------

  override createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    span.setAttribute('data-lexical-dynamic-data', this.__id);
    span.className = 'lexical-dynamic-data';
    return span;
  }

  override updateDOM(prevNode: DynamicDataNode, dom: HTMLElement): boolean {
    if (prevNode.__id !== this.__id) {
      dom.setAttribute('data-lexical-dynamic-data', this.__id);
    }
    return false;
  }

  // Bare token text: what the backend resolves, and what the text transform turns back into a chip on paste
  override exportDOM(): DOMExportOutput {
    return { element: document.createTextNode(buildPlaceholderToken(this.__id)) };
  }

  override isKeyboardSelectable(): boolean {
    return true;
  }

  // ---------- Plain text export ----------

  override getTextContent(): string {
    return buildPlaceholderToken(this.__id);
  }

  // Make it behave like an atomic inline token
  override isInline(): boolean {
    return true;
  }

  override isIsolated(): boolean {
    return true;
  }

  override decorate(): JSX.Element {
    return <DynamicDataChip id={this.__id} nodeKey={this.getKey()} />;
  }
}

// Helpers

export function $createDynamicDataNode(id: string): DynamicDataNode {
  return $create(DynamicDataNode).setId(id);
}

export function $isDynamicDataNode(node: LexicalNode | null | undefined): node is DynamicDataNode {
  return node instanceof DynamicDataNode;
}
